import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import debounce from 'lodash.debounce';
import './AllFires.scss';
import FlatList from 'flatlist-react/lib';
import axios from 'axios';
import { useAlertsContext } from '../../../contexts/AlertsContext';
import { useUserContext } from '../../../contexts/UserContext';
import sortingArrowIcon from '../../../assets/icons/sorting-arrows-icon.png';
import refreshIcon from '../../../assets/icons/refresh-icon.png';
import calendarIcon from '../../../assets/icons/calendar-icon.png';
import Spinner from '../../Spinner';
import HistoricalFire from './HistoricalFire';

const MemoizedHistoricalFire = React.memo(HistoricalFire);

export default function HistoricalFires({
  panel,
  setPanel,
  map,
  user,
  setUser,
  historicalFires,
  setHistoricalFires,
  modal,
  setModal,
}) {
  const { getToken } = useUserContext();
  const { alerts } = useAlertsContext();

  const dateInputRef = useRef(null);
  const openCalendar = () => dateInputRef.current?.showPicker();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isNewestFirst, setIsNewestFirst] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortFilter, setSortFilter] = useState('discovery-time');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const debouncedSetter = useMemo(
    () => debounce((val) => setDebouncedSearch(val), 300),
    []
  );
  useEffect(() => {
    debouncedSetter(search);
    return () => debouncedSetter.cancel();
  }, [search, debouncedSetter]);

  const getHistoricalFires = useCallback(
    (days) => {
      if (!user) return;
      setLoading(true);
      getToken()
        .then((token) =>
          axios
            .get(
              `https://firewatch-api.space-eyes.com/fires/history?area_id=1751&days=${days}`,
              { headers: { Authorization: `Bearer ${token}` } }
            )
            .then((res) => {
              setHistoricalFires(res.data.fires);
              setLoading(false);
            })
            .catch(() => {
              setHistoricalFires([]);
              setLoading(false);
            })
        )
        .catch((err) => {
          console.error('Token error:', err);
          setLoading(false);
        });
    },
    [getToken, setHistoricalFires, user]
  );

  const enhancedFires = useMemo(
    () =>
      (historicalFires || []).map((f) => ({
        ...f,
        nameLower: f.name.toLowerCase(),
        _startMs: new Date(f.startTimestamp).getTime(),
        _updateMs: new Date(f.updatedTimestamp || f.startTimestamp).getTime(),
      })),
    [historicalFires]
  );

  const filteredFires = useMemo(() => {
    const ds = debouncedSearch.toLowerCase();
    
    let base = enhancedFires.filter((f) => f.nameLower.includes(ds));
    
    // Apply status filter
    base = base.filter((f) => {
      if (statusFilter === 'all') return true;
      if (statusFilter === 'active') return f.active;
      if (statusFilter === 'contained') return !f.active;
      return true;
    });

    // Sort fires
    return base.sort((a, b) => {
      switch (sortFilter) {
        case 'acres-burned': {
          const aHas = a.areaAC != null;
          const bHas = b.areaAC != null;
          if (aHas && bHas) {
            return isNewestFirst
              ? b.areaAC - a.areaAC
              : a.areaAC - b.areaAC;
          }
          if (aHas) return -1;
          if (bHas) return 1;
          return 0;
        }

        case 'containment': {
          const aHas = a.perContained != null;
          const bHas = b.perContained != null;
          if (aHas && bHas) {
            return isNewestFirst
              ? b.perContained - a.perContained
              : a.perContained - b.perContained;
          }
          if (aHas) return -1;
          if (bHas) return 1;
          return 0;
        }
        
        case 'discovery-time':
          return isNewestFirst
            ? b._startMs - a._startMs
            : a._startMs - b._startMs;
            
        case 'last-updated':
        default:
          return isNewestFirst
            ? b._updateMs - a._updateMs
            : a._updateMs - b._updateMs;
      }
    });
  }, [enhancedFires, debouncedSearch, statusFilter, sortFilter, isNewestFirst]);

  const calculateDateDifference = useCallback((start, end) => {
    // Convert both dates to start of day in local timezone
    const d1 = new Date(start + 'T00:00:00');
    const d2 = new Date(end.split('T')[0] + 'T00:00:00');
    const diffDays = Math.floor((d2 - d1) / (1000 * 60 * 60 * 24));
    // Ensure we always get at least 0 days (today) to include fires from the selected date
    return Math.max(0, diffDays);
  }, []);

  const getDate30DaysAgo = useCallback(() => {
    const dt = new Date();
    dt.setDate(dt.getDate() - 30);
    return dt.toISOString().split('T')[0];
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    const days = calculateDateDifference(date, new Date().toISOString());
    getHistoricalFires(days).finally(() => setRefreshing(false));
  }, [date, calculateDateDifference, getHistoricalFires]);

  // Only call API when user explicitly changes the date (not on initial load)
  const [hasUserSelectedDate, setHasUserSelectedDate] = useState(false);
  
  useEffect(() => {
    if (hasUserSelectedDate) {
      const days = calculateDateDifference(date, new Date().toISOString());
      getHistoricalFires(days);
    }
  }, [date, calculateDateDifference, getHistoricalFires, hasUserSelectedDate]);

  const renderFireRow = useCallback(
    (fire) => (
      <MemoizedHistoricalFire
        fire={fire}
        panel={panel}
        setPanel={setPanel}
        map={map}
        user={user}
        setUser={setUser}
        modal={modal}
        setModal={setModal}
      />
    ),
    [panel, setPanel, map, user, setUser, modal]
  );

  return (
    <div className="all-fires">
      <input
        type="date"
        value={date}
        min={getDate30DaysAgo()}
        max={new Date().toISOString().split('T')[0]}
        className="all-fires-date-picker"
        ref={dateInputRef}
        onChange={(e) => {
          setDate(e.target.value);
          setHasUserSelectedDate(true);
        }}
      />
      <div
        className="all-fires-date-picker-btn"
        onClick={openCalendar}
      >
        <img className="all-fires-date-picker-btn-icon" src={calendarIcon} alt="calendar" />
        Choose Start Date
      </div>

      {hasUserSelectedDate ? (
        <>
          <div className="all-fires-search">
            <input
              type="text"
              placeholder="Search by fire name"
              className="all-fires-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {loading ? (
            <div style={{ paddingTop: '1rem' }}><Spinner /></div>
          ) : (
            <>  
              <div className="all-fires-filters">
                <div className="all-fires-filters-left">
                  <img
                    src={sortingArrowIcon}
                    className="all-fires-filters-left-icon"
                    style={{
                      transform: isNewestFirst ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s',
                      cursor: 'pointer',
                    }}
                    onClick={() => setIsNewestFirst((x) => !x)}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.1rem' }}>
                    <div style={{ color: 'black', fontWeight: 'bold', fontSize: '0.7rem' }}>SORT</div>
                    <select
                      className="all-fires-filters-left-select"
                      value={sortFilter}
                      onChange={(e) => setSortFilter(e.target.value)}
                    >
                      <option value="acres-burned">Acres Burned</option>
                      <option value="discovery-time">Discovery Time</option>
                      <option value="last-updated">Last Updated</option>
                      <option value="containment">Containment %</option>
                    </select>
                  </div>
                  <img
                    src={refreshIcon}
                    className="all-fires-filters-left-refresh"
                    style={{
                      transform: refreshing ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s',
                      cursor: 'pointer',
                      alignSelf: 'end',
                      marginBottom: 3
                    }}
                    onClick={handleRefresh}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.1rem' }}>
                  <div style={{ color: 'black', fontWeight: 'bold', fontSize: '0.7rem' }}>FILTER</div>
                  <select
                    value={statusFilter}
                    className="all-fires-filters-left-select"
                    onChange={e => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Fires</option>
                    <option value="active">Active Only</option>
                    <option value="contained">Contained Only</option>
                  </select>
                </div>
              </div>

              <div style={{ fontSize: '0.9rem', color: 'black', fontWeight: 'bold', marginTop: '0.7rem' }}>
                {`${filteredFires.length.toLocaleString()} Fires Since ${date}`}
              </div>

              <div className="all-fires-list">
                {refreshing ? (
                  <div style={{ paddingTop: '1rem' }}>
                    <Spinner />
                  </div>
                ) : (
                  <FlatList
                    list={filteredFires}
                    renderItem={renderFireRow}
                    renderOnScroll
                    renderWhenEmpty={() => (
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        padding: '2rem',
                        textAlign: 'center',
                        color: '#666'
                      }}>
                        <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                          No historical fires found
                        </div>
                        <div style={{ fontSize: '0.9rem' }}>
                          Try selecting a different date or adjusting your filter criteria
                        </div>
                      </div>
                    )}
                  />
                )}
              </div>
            </>
          )}
        </>
      ) : (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '2rem',
          textAlign: 'center',
          color: '#666'
        }}>
          <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Select a date to view historical fires
          </div>
          <div style={{ fontSize: '0.9rem' }}>
            Choose a date from the calendar above to see fires from that day onward
          </div>
        </div>
      )}
    </div>
  );
}
